import { cn } from "@/lib/utils";
import { useEffect, useRef, useState } from "react";

/**
 * Cuenta regresiva en vivo hasta `expiresAt` (ISO). Tic cada segundo. Cuando
 * vence muestra "Vencida" en rojo. Pensada para el popup y el centro de
 * mini-tareas: solo formatea tiempo, sin lógica de negocio.
 */
function remainingLabel(expiresAt: string): { text: string; expired: boolean } {
  const target = new Date(expiresAt).getTime();
  const diffMs = target - Date.now();

  if (!Number.isFinite(target) || diffMs <= 0) {
    return { text: "", expired: true };
  }

  const totalSeconds = Math.floor(diffMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const pad = (value: number) => String(value).padStart(2, "0");
  const text =
    hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;

  return { text, expired: false };
}

export function MiniTaskCountdown({
  expiresAt,
  expiredLabel,
  className,
  onExpiredChange,
}: {
  expiresAt: string;
  expiredLabel: string;
  className?: string;
  /**
   * Avisa al padre cuando el estado de vencimiento CAMBIA (solo en la
   * transición, no en cada tic), para que pueda deshabilitar acciones sobre una
   * tarea ya vencida mientras el servidor aún no la barre. Se mantiene estable
   * vía ref para no re-disparar el efecto si el padre pasa un callback nuevo en
   * cada render.
   */
  onExpiredChange?: (expired: boolean) => void;
}) {
  const [, setTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setTick((value) => value + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  const { text, expired } = remainingLabel(expiresAt);

  const onExpiredChangeRef = useRef(onExpiredChange);
  onExpiredChangeRef.current = onExpiredChange;
  const reportedRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (reportedRef.current !== expired) {
      reportedRef.current = expired;
      onExpiredChangeRef.current?.(expired);
    }
  }, [expired]);

  return (
    <span
      className={cn(
        "font-mono tabular-nums",
        expired ? "text-destructive" : "text-foreground",
        className,
      )}
    >
      {expired ? expiredLabel : text}
    </span>
  );
}
